import type { Theme } from './core'
import { defineComponent, inject, provide } from 'vue'
import { useUID } from '../../utils/vue/composables'
import { CSSTheme, getTheme } from './core'

interface DefineThemeConfig {
  id?: string

  /** Creates a watcher function to update the theme */
  watch?: () => Theme
}

interface ThemeScopeProps {
  /**
   * User theme props
   * If not provided, default theme will be used
   */
  theme?: Theme
}

interface InheritedThemeProps {
  /**
   * CSS class of stylesheet and theme
   * Can be used to apply styles to an element
   */
  id: string

  /** Current theme */
  data: Theme
}

const ThemeDataInjectKey = 'get-theme'

export function defineTheme(colors: Theme.CSSColorRecord = {}, config: DefineThemeConfig = {}) {
  const id = config.id || useUID('scoped-theme')

  function generate(props?: Theme) {
    const scope = `.${id}`

    // Normalize props
    const theme = getTheme(props)
    const cssTheme = new CSSTheme(theme)

    const content = `
      @import url('https://fonts.googleapis.com/css2?family=${theme.font}:wght@400;500;600;700&display=swap');
  
      ${scope} ::selection {
        color: ${theme.background};
        background: ${theme.primary};
      }
      
      ${scope} {
        ${cssTheme.getVariablesCSS(colors)};
        font-family: "${theme.font}", Inter !important;
      }
    `

    return content
  }

  const ThemeScope = defineComponent<ThemeScopeProps>((props, ctx) => {
    const slot = () => ctx.slots.default?.()
    const parentTheme = getCurrentThemeSafe()

    // Inherit parent styles
    if (parentTheme) {
      return () => (
        <div class={[parentTheme.id, 'bg-$color-background text-$color-neutral']}>
          {slot()}
        </div>
      )
    }

    // Provide theme data to children
    provide<() => InheritedThemeProps | undefined>(ThemeDataInjectKey, () => {
      const { theme } = props

      if (!theme)
        return

      return { id, data: theme }
    })

    return () => (
      <div class={[id, 'bg-$color-background text-$color-neutral']}>
        <style data-scope={id} innerHTML={generate(props.theme)} />
        {slot()}
      </div>
    )
  }, {
    name: 'ThemeScope',
    props: ['theme']
  })

  return { id, colors, ThemeScope }
}

function getCurrentThemeSafe() {
  return inject<() => InheritedThemeProps | undefined>(ThemeDataInjectKey, () => undefined)()
}

/**
 * Gets current theme in component tree.
 * Cannot be used in the same component where defineTheme is used
 */
export function getCurrentTheme() {
  const data = getCurrentThemeSafe()

  if (!data)
    throw new Error('No theme found for current scope. Make sure defineTheme has been used in parent tree')

  return data
}

/** Inherits the parent theme */
export const InheritTheme = defineComponent((_props, ctx) => {
  return () => {
    const data = getCurrentThemeSafe()

    if (!data)
      console.warn('InheritTheme: No parent theme found')

    return (
      <div class={data?.id}>
        {ctx.slots.default?.()}
      </div>
    )
  }
}, { name: 'InheritTheme' })
